import React, { useEffect, useMemo, useState } from 'react';
import { authFetch } from '../../services/apiServices';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { TRAINING_STATUSES, TRAINING_TYPES, TRAINING_FUNDING_TYPES, DEFAULT_FUNDING_TYPE, trainingStatusLabel, trainingTypeLabel } from '../../constants/trainings';

const createDefaultForm = () => {
  const today = new Date().toISOString().slice(0, 10);
  return {
    name: '',
    description: '',
    type: TRAINING_TYPES[0],
    status: TRAINING_STATUSES[0],
    startDate: today,
    endDate: today,
    location: 'Online',
    trainer: '',
    capacity: '',
    fundingType: DEFAULT_FUNDING_TYPE,
    workingTypeCode: 'TRAINING_INTERNAL',
  };
};

/**
 * @param mode            'create' | 'edit' — ndryshon titullin dhe butonin
 * @param canEditFunding  lloji i financimit ndryshohet vetëm me `trainings.edit`;
 *                        pa të, fusha shfaqet e çkyçur (serveri e refuzon gjithsesi)
 */
export default function AddTrainingDialog({ open, onClose, onSubmit, initialValues, mode = 'create', canEditFunding = true }) {
  const [form, setForm] = useState(initialValues || createDefaultForm());
  // Llojet e punës të kategorisë TRAINING nga fjalori qendror (WorkingType):
  // Internal / External / Unpaid… — dhe çdo tip që admini shton më vonë.
  const [trainingTypes, setTrainingTypes] = useState([]);

  useEffect(() => {
    setForm(initialValues || createDefaultForm());
  }, [initialValues, open]);
  useEffect(() => {
    if (!open) return;
    authFetch('/api/working-types?category=TRAINING').then((r) => r.json()).then((j) => { if (j?.success) setTrainingTypes(j.data.types || []); }).catch(() => {});
  }, [open]);

  const dateError = useMemo(() => {
    if (!form.startDate || !form.endDate) return false;
    return new Date(form.endDate) < new Date(form.startDate);
  }, [form.startDate, form.endDate]);

  const canSubmit = Boolean(form.name.trim()) && !dateError;

  const update = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleSubmit = () => {
    if (!canSubmit) return;
    onSubmit({
      ...form,
      capacity: Number(form.capacity) || 0,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ pb: 1, fontWeight: 700 }}>{mode === 'edit' ? 'Ndrysho trajnimin' : 'Shto trajnim'}</DialogTitle>
      <DialogContent sx={{ pt: 0, pb: 1 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Plotëso detajet bazë për trajnimin e ri. Mund t&apos;i përditësosh edhe më vonë.
        </Typography>
        <Stack spacing={2}>
          <TextField
            autoFocus
            fullWidth
            label="Emri i trajnimit"
            placeholder="p.sh. Siguria kibernetike"
            value={form.name}
            onChange={update('name')}
            required
          />
          <TextField
            fullWidth
            label="Përshkrimi"
            placeholder="(Opsionale) Përshkruaj qëllimin e trajnimit"
            value={form.description}
            onChange={update('description')}
            multiline
            minRows={2}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField select label="Lloji" value={form.type} onChange={update('type')} fullWidth>
              {TRAINING_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {trainingTypeLabel(type)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Statusi"
              value={form.status}
              onChange={update('status')}
              fullWidth
            >
              {TRAINING_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {trainingStatusLabel(status)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Data e fillimit"
              type="date"
              value={form.startDate}
              onChange={update('startDate')}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              label="Data e mbarimit"
              type="date"
              value={form.endDate}
              onChange={update('endDate')}
              InputLabelProps={{ shrink: true }}
              error={dateError}
              helperText={dateError ? 'Data e mbarimit nuk mund të jetë para fillimit' : ' '}
              fullWidth
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Lokacioni"
              value={form.location}
              onChange={update('location')}
              placeholder="Online, On-site..."
              fullWidth
            />
            <TextField
              label="Trajneri"
              value={form.trainer}
              onChange={update('trainer')}
              placeholder="Emri i trajnerit"
              fullWidth
            />
          </Stack>
          <TextField
            label="Kapaciteti"
            type="number"
            inputProps={{ min: 0 }}
            value={form.capacity}
            onChange={update('capacity')}
            placeholder="Numri maksimal i pjesëmarrësve"
            fullWidth
          />
          {trainingTypes.length > 0 && (
            <TextField select label="Lloji i punës (WorkingType)" value={form.workingTypeCode || 'TRAINING_INTERNAL'} onChange={update('workingTypeCode')} fullWidth
              helperText="Si numërohen orët e trajnimit te prezenca dhe paga" inputProps={{ 'data-testid': 'training-working-type' }}>
              {trainingTypes.map((t) => <MenuItem key={t.code} value={t.code}>{t.name}{t.isPaid ? '' : ' (pa pagesë)'}</MenuItem>)}
            </TextField>
          )}
          {/* Lloji i financimit — kodi ruhet, etiketa shfaqet. */}
          <TextField
            select
            label="Financimi"
            value={form.fundingType || DEFAULT_FUNDING_TYPE}
            onChange={update('fundingType')}
            disabled={!canEditFunding}
            helperText={canEditFunding ? 'Kush e paguan trajnimin' : 'Ndryshohet vetëm nga HR-ja ose administratori'}
            fullWidth
            inputProps={{ 'data-testid': 'training-funding' }}
          >
            {TRAINING_FUNDING_TYPES.map((t) => (
              <MenuItem key={t.code} value={t.code}>{t.label}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          Anulo
        </Button>
        <Button variant="contained" onClick={handleSubmit} disabled={!canSubmit}>
          {mode === 'edit' ? 'Ruaj ndryshimet' : 'Krijo trajnimin'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
